import { createContext, useContext, useEffect, useMemo, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { localeOf } from "../i18n";
import axios from "axios";
import {
  buildEmptyDmMatrix,
  buildEmptyIllustrations,
  DEFAULT_ROLES,
  CURRENCIES,
  findCurrency,
  DM_TYPES,
  buildDefaultResourcePlan,
  findLocationPreset,
  buildDefaultBrexPlan,
  BREX_DEFAULT_ROLES,
  buildDefaultDmlPlan,
  findSnsPreset,
  countSnsLevels,
  buildDefaultRiskPlan,
  buildDefaultSchedulePlan,
  buildDefaultBidPlan,
  buildDefaultSbsPlan,
  buildDefaultReviewPlan,
  computeSbsCounts,
} from "../lib/s1000dData";
import { computeTotals, computeResourcing, computeBrex, computeDml, computeReview, combineProjectAndBrex, computeRisk, computeSchedule, computeBidPrice, fmtCurrency, fmtHours, fmtPercent, setActiveLocale } from "../lib/calculations";
import { mergeDmMatrix, mergeIllustrations } from "../lib/matrixImport";
import { sanitizeEstimateForSave, hydrateEstimateFromDoc } from "./estimateSerializers";
import {
  sbsAddSystem, sbsRemoveSystem, sbsUpdateSystem,
  sbsAddSubsystem, sbsRemoveSubsystem, sbsUpdateSubsystem,
  sbsAddSubsubsystem, sbsRemoveSubsubsystem, sbsUpdateSubsubsystem,
  sbsUpdateField, sbsUpdateHoursPerItem, sbsApplyImported, sbsApplyToDmMatrix,
} from "./sbsActions";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api/estimator`;

function authHeaders() {
  const token = localStorage.getItem("token");
  return token ? { headers: { Authorization: `Bearer ${token}` } } : {};
}

const EstimateContext = createContext(null);

export const buildBlankEstimate = () => ({
  id: null,
  projectName: "",
  notes: "",
  country: "AU",
  currency: { ...findCurrency("AUD") },
  overheadPercent: 0,
  contingencyPercent: 0,
  tags: [],
  dmMatrix: buildEmptyDmMatrix(),
  illustrations: buildEmptyIllustrations(),
  roles: DEFAULT_ROLES.map((r) => ({ ...r })),
  resourcePlan: buildDefaultResourcePlan(),
  brexPlan: buildDefaultBrexPlan(),
  dmlPlan: buildDefaultDmlPlan(),
  riskPlan: buildDefaultRiskPlan(),
  schedulePlan: buildDefaultSchedulePlan(),
  bidPlan: buildDefaultBidPlan(),
  sbsPlan: buildDefaultSbsPlan(),
  reviewPlan: buildDefaultReviewPlan(),
});

export function EstimateProvider({ children }) {
  const [estimate, setEstimate] = useState(buildBlankEstimate);
  const [savedList, setSavedList] = useState([]);
  const [rateCards, setRateCards] = useState([]);
  const [loading, setLoading] = useState(false);

  const risk = useMemo(() => computeRisk(estimate.riskPlan), [estimate.riskPlan]);

  // Effective contingency = base contingency + suggested bump (capped at 100% to be safe).
  const effectiveContingencyPercent = useMemo(() => {
    const base = Number(estimate.contingencyPercent) || 0;
    const bump = risk.suggestedContingencyBump || 0;
    return Math.min(100, base + bump);
  }, [estimate.contingencyPercent, risk.suggestedContingencyBump]);

  const totals = useMemo(
    () => computeTotals({ ...estimate, contingencyPercent: effectiveContingencyPercent }),
    [estimate, effectiveContingencyPercent]
  );
  const resourcing = useMemo(() => computeResourcing(totals, estimate.resourcePlan), [totals, estimate.resourcePlan]);
  const brex = useMemo(
    () => computeBrex(estimate.brexPlan, estimate.overheadPercent, effectiveContingencyPercent),
    [estimate.brexPlan, estimate.overheadPercent, effectiveContingencyPercent]
  );
  const dml = useMemo(
    () => computeDml(estimate.dmlPlan, totals, totals.blendedRate, estimate.overheadPercent, effectiveContingencyPercent),
    [estimate.dmlPlan, totals, estimate.overheadPercent, effectiveContingencyPercent]
  );
  const review = useMemo(
    () => computeReview(totals, estimate.reviewPlan),
    [totals, estimate.reviewPlan]
  );
  const combined = useMemo(
    () => combineProjectAndBrex(
      totals, brex, estimate.overheadPercent, effectiveContingencyPercent,
      !!estimate.brexPlan?.includeInProjectTotal, dml, review
    ),
    [totals, brex, estimate.overheadPercent, effectiveContingencyPercent, estimate.brexPlan?.includeInProjectTotal, dml, review]
  );
  const schedule = useMemo(() => {
    const basisHours = estimate.schedulePlan?.basisHours === "project" ? totals.totalHours : combined.combinedHours;
    return computeSchedule(estimate.schedulePlan, basisHours, estimate.resourcePlan?.startDate, estimate.resourcePlan?.endDate);
  }, [estimate.schedulePlan, totals.totalHours, combined.combinedHours, estimate.resourcePlan?.startDate, estimate.resourcePlan?.endDate]);
  const bidPrice = useMemo(() => {
    const cost = estimate.bidPlan?.pricingBasis === "project" ? totals.grandTotal : combined.combinedGrandTotal;
    return computeBidPrice(cost, estimate.bidPlan);
  }, [estimate.bidPlan, totals.grandTotal, combined.combinedGrandTotal]);

  const updateDmlPlan = (field, value) =>
    setEstimate((e) => ({ ...e, dmlPlan: { ...e.dmlPlan, [field]: value } }));

  // ---- Risk Plan ----
  const updateReviewPlan = (field, value) =>
    setEstimate((e) => ({ ...e, reviewPlan: { ...e.reviewPlan, [field]: value } }));

  const updateRiskPlanField = (field, value) =>
    setEstimate((e) => ({ ...e, riskPlan: { ...e.riskPlan, [field]: value } }));

  const addRisk = () =>
    setEstimate((e) => ({
      ...e,
      riskPlan: {
        ...e.riskPlan,
        risks: [
          ...e.riskPlan.risks,
          { id: `r${Date.now()}`, title: "New risk", category: "technical", likelihood: 3, impact: 3, mitigation: "", ownerRole: "" },
        ],
      },
    }));

  const updateRisk = (id, field, value) =>
    setEstimate((e) => ({
      ...e,
      riskPlan: {
        ...e.riskPlan,
        risks: e.riskPlan.risks.map((r) =>
          r.id === id
            ? { ...r, [field]: ["likelihood", "impact"].includes(field) ? Number(value) : value }
            : r
        ),
      },
    }));

  const removeRisk = (id) =>
    setEstimate((e) => ({ ...e, riskPlan: { ...e.riskPlan, risks: e.riskPlan.risks.filter((r) => r.id !== id) } }));

  const addAssumption = (text) =>
    setEstimate((e) => ({
      ...e,
      riskPlan: {
        ...e.riskPlan,
        assumptions: [...e.riskPlan.assumptions, { id: `a${Date.now()}`, text: text || "New assumption" }],
      },
    }));
  const updateAssumption = (id, text) =>
    setEstimate((e) => ({
      ...e,
      riskPlan: { ...e.riskPlan, assumptions: e.riskPlan.assumptions.map((a) => (a.id === id ? { ...a, text } : a)) },
    }));
  const removeAssumption = (id) =>
    setEstimate((e) => ({
      ...e,
      riskPlan: { ...e.riskPlan, assumptions: e.riskPlan.assumptions.filter((a) => a.id !== id) },
    }));

  const addDependency = () =>
    setEstimate((e) => ({
      ...e,
      riskPlan: {
        ...e.riskPlan,
        dependencies: [...e.riskPlan.dependencies, { id: `d${Date.now()}`, text: "New dependency", type: "Internal" }],
      },
    }));
  const updateDependency = (id, field, value) =>
    setEstimate((e) => ({
      ...e,
      riskPlan: {
        ...e.riskPlan,
        dependencies: e.riskPlan.dependencies.map((d) => (d.id === id ? { ...d, [field]: value } : d)),
      },
    }));
  const removeDependency = (id) =>
    setEstimate((e) => ({
      ...e,
      riskPlan: { ...e.riskPlan, dependencies: e.riskPlan.dependencies.filter((d) => d.id !== id) },
    }));

  // ---- Schedule Plan ----
  const updateSchedulePhase = (key, field, value) =>
    setEstimate((e) => ({
      ...e,
      schedulePlan: {
        ...e.schedulePlan,
        phases: {
          ...e.schedulePlan.phases,
          [key]: { ...e.schedulePlan.phases[key], [field]: value === "" ? "" : Number(value) },
        },
      },
    }));
  const setScheduleBasis = (basis) =>
    setEstimate((e) => ({ ...e, schedulePlan: { ...e.schedulePlan, basisHours: basis } }));

  // ---- Bid Plan ----
  const applyImportedRiskPlan = (parsed, mode = "replace") => {
    setEstimate((e) => {
      const base = mode === "merge" ? e.riskPlan : { ...e.riskPlan, risks: [], assumptions: [], dependencies: [] };
      return {
        ...e,
        riskPlan: {
          ...base,
          risks: [...(base.risks || []), ...(parsed.risks || [])],
          assumptions: [...(base.assumptions || []), ...(parsed.assumptions || [])],
          dependencies: [...(base.dependencies || []), ...(parsed.dependencies || [])],
        },
      };
    });
  };

  const updateBidField = (field, value) =>
    setEstimate((e) => ({
      ...e,
      bidPlan: { ...e.bidPlan, [field]: ["pricingBasis"].includes(field) ? value : (value === "" ? "" : Number(value)) },
    }));

  // ---- System Breakdown Structure (SBS) ----
  const sbsCounts = useMemo(
    () => computeSbsCounts(estimate.sbsPlan),
    [estimate.sbsPlan]
  );

  const addSystem = () => setEstimate(sbsAddSystem);
  const removeSystem = (sysId) => setEstimate((e) => sbsRemoveSystem(e, sysId));
  const updateSystem = (sysId, field, value) => setEstimate((e) => sbsUpdateSystem(e, sysId, field, value));

  const addSubsystem = (sysId) => setEstimate((e) => sbsAddSubsystem(e, sysId));
  const removeSubsystem = (sysId, subId) => setEstimate((e) => sbsRemoveSubsystem(e, sysId, subId));
  const updateSubsystem = (sysId, subId, field, value) =>
    setEstimate((e) => sbsUpdateSubsystem(e, sysId, subId, field, value));

  const addSubsubsystem = (sysId, subId) => setEstimate((e) => sbsAddSubsubsystem(e, sysId, subId));
  const removeSubsubsystem = (sysId, subId, ssId) => setEstimate((e) => sbsRemoveSubsubsystem(e, sysId, subId, ssId));
  const updateSubsubsystem = (sysId, subId, ssId, field, value) =>
    setEstimate((e) => sbsUpdateSubsubsystem(e, sysId, subId, ssId, field, value));

  const updateSbsField = (field, value) => setEstimate((e) => sbsUpdateField(e, field, value));
  const updateSbsHoursPerItem = (level, value) => setEstimate((e) => sbsUpdateHoursPerItem(e, level, value));
  const applyImportedSbs = (parsed, mode = "replace") => setEstimate((e) => sbsApplyImported(e, parsed, mode));
  const applySbsToDmMatrix = (mode = "replace") => setEstimate((e) => sbsApplyToDmMatrix(e, mode));

  // ---- Matrix import (CSV / XLSX round-trip) ----
  const applyImportedDmMatrix = (matrix, mode = "replace") => {
    setEstimate((e) => ({
      ...e,
      dmMatrix: mode === "merge" ? mergeDmMatrix(e.dmMatrix, matrix) : matrix,
    }));
  };

  const applyImportedIllustrations = (illus, mode = "replace") => {
    setEstimate((e) => ({
      ...e,
      illustrations: mode === "merge" ? mergeIllustrations(e.illustrations, illus) : illus,
    }));
  };

  const applySnsPreset = (presetId) => {
    const preset = findSnsPreset(presetId);
    const counts = countSnsLevels(preset);
    setEstimate((e) => ({
      ...e,
      dmlPlan: {
        ...e.dmlPlan,
        snsPresetId: preset.id,
        systemCount: counts.systems,
        subsystemCount: counts.subsystems,
        subsubsystemCount: counts.subsubsystems,
      },
    }));
  };

  const currencySymbol = estimate.currency?.symbol || "$";
  const { i18n: i18nInstance } = useTranslation();
  const currentLocale = localeOf(i18nInstance.resolvedLanguage || i18nInstance.language);
  // Keep the module-level locale in sync so lower-level fmt* helpers used directly
  // by tabs (without going through the context) also produce localised output.
  useEffect(() => { setActiveLocale(currentLocale); }, [currentLocale]);
  const formatCurrency = useCallback(
    (v) => fmtCurrency(v, currencySymbol, currentLocale),
    [currencySymbol, currentLocale]
  );
  const formatHours = useCallback((v) => fmtHours(v, currentLocale), [currentLocale]);
  const formatPercent = useCallback((v) => fmtPercent(v, currentLocale), [currentLocale]);
  const formatDate = useCallback((iso) => {
    if (!iso) return "";
    try {
      return new Date(iso).toLocaleDateString(currentLocale, { year: "numeric", month: "short", day: "2-digit" });
    } catch {
      return iso;
    }
  }, [currentLocale]);

  const setProjectName = (name) => setEstimate((e) => ({ ...e, projectName: name }));
  const setNotes = (notes) => setEstimate((e) => ({ ...e, notes }));
  const setCountry = (country) => setEstimate((e) => ({ ...e, country }));
  const setCurrency = (code) => setEstimate((e) => ({ ...e, currency: { ...findCurrency(code) } }));
  const setOverheadPercent = (v) =>
    setEstimate((e) => ({ ...e, overheadPercent: v === "" ? "" : Number(v) }));
  const setContingencyPercent = (v) =>
    setEstimate((e) => ({ ...e, contingencyPercent: v === "" ? "" : Number(v) }));

  const setTags = (tags) => setEstimate((e) => ({ ...e, tags: Array.isArray(tags) ? tags : [] }));
  const addTag = (tag) => {
    const t = String(tag || "").trim();
    if (!t) return;
    setEstimate((e) => (e.tags.includes(t) ? e : { ...e, tags: [...e.tags, t] }));
  };
  const removeTag = (tag) =>
    setEstimate((e) => ({ ...e, tags: e.tags.filter((t) => t !== tag) }));

  const updateResourcePlan = (field, value) =>
    setEstimate((e) => ({
      ...e,
      resourcePlan: { ...e.resourcePlan, [field]: value },
    }));

  const applyLocationPreset = (key) => {
    const preset = findLocationPreset(key);
    setEstimate((e) => ({
      ...e,
      resourcePlan: {
        ...e.resourcePlan,
        locationPreset: preset.key,
        weekendDays: [...preset.weekendDays],
        publicHolidays: preset.publicHolidays,
      },
    }));
  };

  const toggleWeekendDay = (dayIdx) =>
    setEstimate((e) => {
      const current = new Set(e.resourcePlan.weekendDays || []);
      if (current.has(dayIdx)) current.delete(dayIdx); else current.add(dayIdx);
      return {
        ...e,
        resourcePlan: {
          ...e.resourcePlan,
          locationPreset: "CUSTOM",
          weekendDays: Array.from(current).sort((a, b) => a - b),
        },
      };
    });

  // --- BREX / Business Rules ---
  const updateBrexPlan = (field, value) =>
    setEstimate((e) => ({
      ...e,
      brexPlan: { ...e.brexPlan, [field]: value },
    }));

  const toggleBrexCategory = (catId) =>
    setEstimate((e) => ({
      ...e,
      brexPlan: {
        ...e.brexPlan,
        categoryInclusions: {
          ...e.brexPlan.categoryInclusions,
          [catId]: !(e.brexPlan.categoryInclusions?.[catId] !== false),
        },
      },
    }));

  const setBrexCategoryDps = (catId, value) =>
    setEstimate((e) => ({
      ...e,
      brexPlan: {
        ...e.brexPlan,
        categoryDpOverrides: {
          ...e.brexPlan.categoryDpOverrides,
          [catId]: value === "" ? "" : Number(value),
        },
      },
    }));

  const updateBrexRole = (idx, field, value) =>
    setEstimate((e) => ({
      ...e,
      brexPlan: {
        ...e.brexPlan,
        roles: e.brexPlan.roles.map((r, i) =>
          i === idx
            ? { ...r, [field]: field === "name" ? value : (value === "" ? "" : Number(value)) }
            : r
        ),
      },
    }));

  const addBrexRole = () =>
    setEstimate((e) => ({
      ...e,
      brexPlan: {
        ...e.brexPlan,
        roles: [...e.brexPlan.roles, { name: "New Role", hourlyRate: 100, effortPercent: 0 }],
      },
    }));

  const removeBrexRole = (idx) =>
    setEstimate((e) => ({
      ...e,
      brexPlan: { ...e.brexPlan, roles: e.brexPlan.roles.filter((_, i) => i !== idx) },
    }));

  const resetBrexRoles = () =>
    setEstimate((e) => ({
      ...e,
      brexPlan: { ...e.brexPlan, roles: BREX_DEFAULT_ROLES.map((r) => ({ ...r })) },
    }));

  const setAllBrexCategoriesIncluded = (included) =>
    setEstimate((e) => ({
      ...e,
      brexPlan: {
        ...e.brexPlan,
        categoryInclusions: Object.fromEntries(
          Object.keys(e.brexPlan.categoryInclusions).map((k) => [k, included])
        ),
      },
    }));

  const updateDmCell = (code, level, field, value) =>
    setEstimate((e) => ({
      ...e,
      dmMatrix: {
        ...e.dmMatrix,
        [code]: {
          ...e.dmMatrix[code],
          [level]: {
            ...e.dmMatrix[code][level],
            [field]: value === "" ? "" : Number(value),
          },
        },
      },
    }));

  const updateIllusCell = (level, field, value) =>
    setEstimate((e) => ({
      ...e,
      illustrations: {
        ...e.illustrations,
        [level]: {
          ...e.illustrations[level],
          [field]: value === "" ? "" : Number(value),
        },
      },
    }));

  const updateRole = (idx, field, value) =>
    setEstimate((e) => ({
      ...e,
      roles: e.roles.map((r, i) =>
        i === idx ? { ...r, [field]: field === "name" ? value : (value === "" ? "" : Number(value)) } : r
      ),
    }));

  const addRole = () =>
    setEstimate((e) => ({
      ...e,
      roles: [...e.roles, { name: "New Role", hourlyRate: 100, effortPercent: 0 }],
    }));

  const removeRole = (idx) =>
    setEstimate((e) => ({
      ...e,
      roles: e.roles.filter((_, i) => i !== idx),
    }));

  const resetEstimate = () => setEstimate(buildBlankEstimate());

  const duplicateEstimate = () => {
    setEstimate((e) => ({
      ...e,
      id: null,
      projectName: e.projectName ? `Copy of ${e.projectName}` : "Untitled Copy",
    }));
  };

  // Apply a rate card to the current estimate (preserves quantities, replaces rates/defaults)
  const applyRateCard = (rc) => {
    setEstimate((e) => {
      // Update DM hour defaults across all type rows (preserve qty)
      const newMatrix = {};
      const dmDefaults = rc.dmDefaultHours || { simple: 6, intermediate: 8, complex: 12 };
      for (const t of DM_TYPES) {
        const row = e.dmMatrix[t.code] || {};
        newMatrix[t.code] = {
          simple: { qty: row.simple?.qty || 0, hours: Number(dmDefaults.simple) || 0 },
          intermediate: { qty: row.intermediate?.qty || 0, hours: Number(dmDefaults.intermediate) || 0 },
          complex: { qty: row.complex?.qty || 0, hours: Number(dmDefaults.complex) || 0 },
        };
      }
      const ilDefaults = rc.illustrationDefaultHours || { simple: 4, intermediate: 8, complex: 12 };
      return {
        ...e,
        currency: rc.currency || e.currency,
        overheadPercent: Number(rc.overheadPercent) || 0,
        contingencyPercent: Number(rc.contingencyPercent) || 0,
        roles: (rc.roles && rc.roles.length > 0) ? rc.roles.map((r) => ({ ...r })) : e.roles,
        dmMatrix: newMatrix,
        illustrations: {
          simple: { qty: e.illustrations.simple?.qty || 0, hours: Number(ilDefaults.simple) || 0 },
          intermediate: { qty: e.illustrations.intermediate?.qty || 0, hours: Number(ilDefaults.intermediate) || 0 },
          complex: { qty: e.illustrations.complex?.qty || 0, hours: Number(ilDefaults.complex) || 0 },
        },
      };
    });
  };

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/estimates`, authHeaders());
      setSavedList(res.data || []);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchRateCards = useCallback(async () => {
    const res = await axios.get(`${API}/ratecards`, authHeaders());
    setRateCards(res.data || []);
  }, []);

  useEffect(() => {
    fetchList();
    fetchRateCards();
  }, [fetchList, fetchRateCards]);

  const sanitizeForSave = (est) => sanitizeEstimateForSave(est, totals);

  const saveEstimate = async () => {
    const payload = sanitizeForSave(estimate);
    let saved;
    if (estimate.id) {
      const res = await axios.put(`${API}/estimates/${estimate.id}`, payload, authHeaders());
      saved = res.data;
    } else {
      const res = await axios.post(`${API}/estimates`, payload, authHeaders());
      saved = res.data;
    }
    setEstimate((e) => ({ ...e, id: saved.id }));
    await fetchList();
    return saved;
  };

  const hydrateFromDoc = (d) => hydrateEstimateFromDoc(d);

  const loadEstimate = async (id) => {
    const res = await axios.get(`${API}/estimates/${id}`, authHeaders());
    setEstimate(hydrateFromDoc(res.data));
  };

  const deleteEstimate = async (id) => {
    await axios.delete(`${API}/estimates/${id}`, authHeaders());
    if (estimate.id === id) resetEstimate();
    await fetchList();
  };

  const duplicateFromSaved = async (id) => {
    const res = await axios.get(`${API}/estimates/${id}`, authHeaders());
    const d = res.data;
    setEstimate({
      ...hydrateFromDoc(d),
      id: null,
      projectName: d.projectName ? `Copy of ${d.projectName}` : "Untitled Copy",
    });
  };

  // ----- Revision API helpers -----
  const listRevisions = async (id) => {
    const res = await axios.get(`${API}/estimates/${id}/revisions`, authHeaders());
    return res.data || [];
  };

  const restoreRevision = async (id, revisionNumber) => {
    const res = await axios.post(`${API}/estimates/${id}/revisions/${revisionNumber}/restore`, {}, authHeaders());
    setEstimate(hydrateFromDoc(res.data));
    await fetchList();
    return res.data;
  };

  // ----- Rate card API helpers -----
  const createRateCard = async (payload) => {
    const res = await axios.post(`${API}/ratecards`, payload, authHeaders());
    await fetchRateCards();
    return res.data;
  };
  const updateRateCard = async (id, payload) => {
    const res = await axios.put(`${API}/ratecards/${id}`, payload, authHeaders());
    await fetchRateCards();
    return res.data;
  };
  const deleteRateCard = async (id) => {
    await axios.delete(`${API}/ratecards/${id}`, authHeaders());
    await fetchRateCards();
  };

  const submitRateCard = async (id, submittedBy) => {
    const { data } = await axios.post(`${API}/ratecards/${id}/submit`, { submittedBy: submittedBy || "" }, authHeaders());
    await fetchRateCards();
    return data;
  };

  const approveRateCard = async (id, approvedBy) => {
    const { data } = await axios.post(`${API}/ratecards/${id}/approve`, { approvedBy: approvedBy || "" }, authHeaders());
    await fetchRateCards();
    return data;
  };

  const rejectRateCard = async (id, reason) => {
    const { data } = await axios.post(`${API}/ratecards/${id}/reject`, { reason: reason || "" }, authHeaders());
    await fetchRateCards();
    return data;
  };

  const fetchRateCardHistory = async (id) => {
    const { data } = await axios.get(`${API}/ratecards/${id}/history`, authHeaders());
    return data;
  };

  const value = {
    estimate,
    totals,
    resourcing,
    brex,
    combined,
    dml,
    review,
    risk,
    schedule,
    bidPrice,
    effectiveContingencyPercent,
    savedList,
    rateCards,
    loading,
    formatCurrency,
    formatHours,
    formatPercent,
    formatDate,
    currentLocale,
    currencySymbol,
    setProjectName,
    setNotes,
    setCountry,
    setCurrency,
    setOverheadPercent,
    setContingencyPercent,
    setTags,
    addTag,
    removeTag,
    updateResourcePlan,
    applyLocationPreset,
    toggleWeekendDay,
    updateBrexPlan,
    toggleBrexCategory,
    setBrexCategoryDps,
    updateBrexRole,
    addBrexRole,
    removeBrexRole,
    resetBrexRoles,
    setAllBrexCategoriesIncluded,
    updateDmlPlan,
    updateReviewPlan,
    applySnsPreset,
    updateDmCell,
    updateIllusCell,
    updateRole,
    addRole,
    removeRole,
    resetEstimate,
    saveEstimate,
    loadEstimate,
    deleteEstimate,
    duplicateEstimate,
    duplicateFromSaved,
    fetchList,
    listRevisions,
    restoreRevision,
    fetchRateCards,
    createRateCard,
    updateRateCard,
    deleteRateCard,
    submitRateCard,
    approveRateCard,
    rejectRateCard,
    fetchRateCardHistory,
    applyRateCard,
    updateRiskPlanField,
    addRisk,
    updateRisk,
    removeRisk,
    addAssumption,
    updateAssumption,
    removeAssumption,
    addDependency,
    updateDependency,
    removeDependency,
    updateSchedulePhase,
    setScheduleBasis,
    updateBidField,
    applyImportedDmMatrix,
    applyImportedIllustrations,
    applyImportedRiskPlan,
    sbsCounts,
    addSystem,
    removeSystem,
    updateSystem,
    addSubsystem,
    removeSubsystem,
    updateSubsystem,
    addSubsubsystem,
    removeSubsubsystem,
    updateSubsubsystem,
    updateSbsField,
    updateSbsHoursPerItem,
    applySbsToDmMatrix,
    applyImportedSbs,
  };

  return <EstimateContext.Provider value={value}>{children}</EstimateContext.Provider>;
}

export function useEstimate() {
  const ctx = useContext(EstimateContext);
  if (!ctx) throw new Error("useEstimate must be used inside EstimateProvider");
  return ctx;
}
